import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Menu as MenuType, MenuItem, SubMenu } from 'types/capi/plp';

import { pillClassName } from '~/components/Pill';
import useFilterInteraction from '~/hooks/useFilterInteraction';
import useRegion from '~/hooks/useRegion';
import type { TrackedButtonClickName } from '~/services/gtm.client';
import gtm from '~/services/gtm.client';
import type { Region } from '~/utils/region';

import Dropdown from '../Dropdown';
import MenuOptionsListItem from './MenuOptionsListItem';
import SizeFilterGrid from './SizeMenuGrid';

export type FacetSelection = { key?: string; text: string; value?: string };

const SEARCHABLE_ITEM_COUNT = 7;

function sizeRegionFor(region: Region) {
  return region.code === 'GB' ? 'UK' : 'EU';
}

type Props = {
  menus: MenuType[];
};

function getMenuDisplayTitle({
  activeItem,
  menu,
  region,
  translate
}: {
  activeItem: MenuItem | undefined;
  menu: MenuType | SubMenu;
  region: Region;
  translate: TFunction<'plp', undefined>;
}) {
  if (menu.id === 'SIZE_MENU') {
    return activeItem
      ? `${sizeRegionFor(region)} ${activeItem.text}`
      : translate('size');
  }

  if (!activeItem) {
    return menu.id === 'GENDER_MENU' ? translate('gender') : menu.title;
  }

  return activeItem.text;
}

function getEventTitleName(title: string) {
  return title === 'Discover' ? 'Gender' : title;
}

function FilterDropdown({
  menu,
  activeItem
}: {
  activeItem?: MenuItem;
  menu: MenuType | SubMenu;
}) {
  const { t } = useTranslation('plp');
  const region = useRegion();
  const fireFilterInteraction = useFilterInteraction();
  const [query, setQuery] = useState('');
  const isSizeFilter = menu.id === 'SIZE_MENU';
  const isSearchable =
    !isSizeFilter && menu.items.length > SEARCHABLE_ITEM_COUNT;
  const items = isSearchable
    ? menu.items.filter((item) =>
        item.text.toLowerCase().includes(query.toLowerCase())
      )
    : menu.items;

  const eventTitleName = getEventTitleName(menu.title);

  function onMenuOpen() {
    return gtm.trackedButtonClick({
      buttonName: eventTitleName as TrackedButtonClickName,
      pagePosition: 'Top Bar'
    });
  }

  function onOptionSelect({ key, text, value }: FacetSelection) {
    gtm.sendPlpProductFilterEvent({
      filterName: eventTitleName,
      filterValue: text,
      pagePosition: 'Filter Toolbar'
    });

    // CAPI sends key and value together or not at all, so this is not a
    // half-pair guard: no pair means the taxonomy item has no id and the
    // selection has nothing joinable to report. Rare, and the same item is
    // already absent from the render's applied_filters, so both sides of the
    // join stay consistent rather than one going quiet on its own.
    if (key && value) fireFilterInteraction({ key, value }, 'facet_nav');
  }

  const menuDisplayTitle = getMenuDisplayTitle({
    activeItem,
    menu,
    region,
    translate: t
  });

  const isFilled = Boolean(activeItem) || isSizeFilter;

  return (
    <Dropdown
      chevronStroke={isFilled ? '#FFFFFF' : '#A9A9A9'}
      className="z-40 w-fit max-w-lg"
      fitContent={isSizeFilter}
      name={menu.id}
      onOpen={onMenuOpen}
      search={
        isSearchable
          ? { onChange: (event) => setQuery(event.target.value), value: query }
          : undefined
      }
      selectedValue={activeItem?.value ?? activeItem?.text}
      trigger={<span>{menuDisplayTitle}</span>}
      triggerClassName={pillClassName({
        active: isFilled,
        tone: activeItem ? 'dark' : 'forest'
      })}
    >
      {(close) =>
        isSizeFilter ? (
          <SizeFilterGrid
            items={items}
            menuId={menu.id}
            onOptionSelect={(selection) => {
              onOptionSelect(selection);
              close();
            }}
            sizeRegion={sizeRegionFor(region)}
          />
        ) : items.length ? (
          items.map((item, index) => (
            <MenuOptionsListItem
              item={item}
              key={`${item.path}-${index}`}
              menuId={menu.id}
              onSelect={(selection) => {
                onOptionSelect(selection);
                close();
              }}
            />
          ))
        ) : (
          <p className="p-4 text-center text-sm text-grey-hide">
            {t('noSearchResults')}
          </p>
        )
      }
    </Dropdown>
  );
}

function Menu({ menu }: { menu: MenuType }) {
  const activeMenu = menu.items.find((i) => menu.active && i.active);

  return (
    <>
      <FilterDropdown menu={menu} activeItem={activeMenu} />
      {activeMenu?.sub_menus?.map((subMenu) => (
        <FilterDropdown
          menu={subMenu}
          activeItem={subMenu.items.find((i) => subMenu.active && i.active)}
          key={subMenu.id}
        />
      ))}
    </>
  );
}

export default function Menus({ menus }: Props) {
  return menus.map((m) => <Menu key={m.id} menu={m} />);
}
